import { useCallback, useState } from "react"

import { useDidUpdate } from "@mantine/hooks"
import { httpsCallable } from "firebase/functions"

import { useAuth, User } from "../../../contexts/authentication-context"
import { firebase } from "../../../services/firebase/firebase"
import { useCloudFunction } from "../../../hooks/useCloudFunction"
import { useLanguage } from "../../../contexts/language-context"
import { AuthUpdateData } from "../../../functions/auth/auth-update"
import { useUsers } from "../../../contexts/users-context"

export default (user: User) => {
    const { L } = useLanguage()
    const { user: currentUser } = useAuth()
    const [groupClaims, setGroupClaims] = useState<string[] | true>(user.groups)
    const [roleClaims, setRoleClaims] = useState<string[] | true>(user.roles)
    const { update } = useUsers()
    const {
        onStart: onUpdateStart,
        onSuccess: onUpdateSuccess,
        onError: onUpdateError,
        processing: processingUpdate,
    } = useCloudFunction("auth-update")
    const {
        onStart: onDeleteStart,
        onSuccess: onDeleteSuccess,
        onError: onDeleteError,
        processing: processingDelete,
    } = useCloudFunction("auth-delete")

    const onDelete = useCallback(() => {
        if (user.uid === currentUser?.uid) return

        onDeleteStart(L("Deleting user..."))
        httpsCallable(
            firebase.functions,
            "auth-delete",
        )({ id: user.uid })
            .then(update)
            .then(onDeleteSuccess(L("User deleted successfully!")))
            .catch(onDeleteError(L("Deleting user failed!")))
    }, [L, user, update])

    useDidUpdate(() => {
        if (user.uid === currentUser?.uid) return

        onUpdateStart(L("Updating %s's privileges...", user.displayName ?? "user"))
        httpsCallable<AuthUpdateData>(
            firebase.functions,
            "auth-update",
        )({ id: user.uid, organisation: user.organisation, groups: groupClaims, roles: roleClaims })
            .then(update)
            .then(onUpdateSuccess())
            .catch(onUpdateError())
    }, [groupClaims, roleClaims])

    return { groupClaims, setGroupClaims, roleClaims, setRoleClaims, onDelete, processingDelete, processingUpdate }
}
